import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  apiSpec,
  type EligibilitySchemaWithDistribution,
  type SignatureSchema,
  TokenDistributionPathSchema,
  type TokenDistributionSchema,
  TokenEligibilitiesUrlParams,
  UserUrlParam,
} from '@rhinofi/token-distribution-api-spec'
import { Effect } from 'effect'
import { HttpApiClient } from 'effect/http-api'
import { envConfig } from '../../../env/envConfig'
import { runApiRequest } from '../../../services/api/runApiRequest'

const apiClient = HttpApiClient.make(apiSpec, { baseUrl: `${envConfig.tradingApi}/token-distribution` })

type DistributionArgs = { chain: string; distributionId: number }

export const tokenDistributionApi = createApi({
  reducerPath: 'tokenDistributionApi',
  baseQuery: fetchBaseQuery({}),
  endpoints: (builder) => ({
    tokenDistributionUserEligibilityMany: builder.query<
      readonly EligibilitySchemaWithDistribution[],
      { user: string; distribution: readonly DistributionArgs[] }
    >({
      queryFn: ({ user, distribution }) =>
        runApiRequest(
          Effect.gen(function* () {
            const client = yield* apiClient
            return yield* client.tokenDistribution.userEligibilityMany({
              query: yield* TokenEligibilitiesUrlParams.makeEffect({ user, distribution }),
            })
          }),
        ),
    }),
    tokenDistributionUserSignature: builder.query<SignatureSchema, DistributionArgs & { user: string }>({
      queryFn: ({ chain, distributionId, user }) =>
        runApiRequest(
          Effect.gen(function* () {
            const client = yield* apiClient
            return yield* client.tokenDistribution.userSignature({
              params: yield* TokenDistributionPathSchema.makeEffect({ chain, distributionId }),
              query: yield* UserUrlParam.makeEffect({ user }),
            })
          }),
        ),
    }),
    tokenDistributionGet: builder.query<TokenDistributionSchema, DistributionArgs>({
      queryFn: ({ chain, distributionId }) =>
        runApiRequest(
          Effect.gen(function* () {
            const client = yield* apiClient
            return yield* client.tokenDistribution.get({
              params: yield* TokenDistributionPathSchema.makeEffect({ chain, distributionId }),
            })
          }),
        ),
    }),
  }),
})

export const {
  useTokenDistributionUserEligibilityManyQuery,
  useLazyTokenDistributionGetQuery,
  useLazyTokenDistributionUserSignatureQuery: useLazyTokenDistributionSignatureQuery,
} = tokenDistributionApi
